"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, X, UserCheck, ShieldCheck, Zap, ArrowRight, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const comparisonPoints = [{
  id: 1,
  title: "Senior Direct Access",
  consultantBenefit: "You work directly with me—an experienced specialist crafting and executing your campaign end-to-end.",
  agencyDrawback: "Your account is assigned to junior staff or interns, managed through a non-technical account manager.",
  highlightTag: "100% Direct Expert Time"
}, {
  id: 2,
  title: "Bespoke Growth Strategy",
  consultantBenefit: "I build tailored, search strategy frameworks designed specifically for your target revenue and tech stack.",
  agencyDrawback: "One-size-fits-all audit templates and standardized monthly checklists recycled across accounts.",
  highlightTag: "Zero Template Work"
}, {
  id: 3,
  title: "Maximum Retainer Efficiency",
  consultantBenefit: "Every pound of your investment goes directly into strategy, technical execution, and high-impact work.",
  agencyDrawback: "Up to 60% of fees pay for agency overhead, office rent, sales teams, and management layers.",
  highlightTag: "Cost Efficient"
}, {
  id: 4,
  title: "Rapid Execution Velocity",
  consultantBenefit: "Direct communication with me means instant feedback, fast technical fixes, and swift deployment.",
  agencyDrawback: "Bureaucratic sign-off chains, scheduled ticket queues, and slow multi-week turnaround times.",
  highlightTag: "Agile Execution"
}, {
  id: 5,
  title: "Commercial Focus & Clarity",
  consultantBenefit: "I measure success strictly on metrics that drive business growth: conversions, leads, and revenue.",
  agencyDrawback: "Vanity metrics, automated PDF reports, and keyword volume highlights that conceal low revenue impact.",
  highlightTag: "Revenue Focused"
}, {
  id: 6,
  title: "Strict Client Ratios",
  consultantBenefit: "I deliberately limit how many accounts I accept so your business receives deep, focused attention.",
  agencyDrawback: "Account leads juggling 15–20 clients simultaneously, forcing your account onto autopilot.",
  highlightTag: "Undivided Care"
}];
export default function Consultant() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-secondary/60 blur-[120px] rounded-full pointer-events-none -z-10" />
      <div className="absolute bottom-10 right-10 w-72 h-72 bg-accent/10 blur-[90px] rounded-full pointer-events-none -z-10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 border border-accent/30 text-foreground font-medium">
            <UserCheck className="w-3.5 h-3.5 mr-1.5 text-accent" />
            Direct Consultant Access vs Agency Model
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground mb-6">
            Why Work Directly With Me?
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            When you hire an agency, you often pay for account managers, executive overhead, and pitch decks. 
            When you partner with me, you work strictly with a senior independent SEO strategist focused on your bottom line.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {comparisonPoints.map((item, i) => <motion.div key={item.id} variants={itemVariants} data-index={i} className="h-full">
              <Card className="h-full flex flex-col border border-border bg-card/80 hover:bg-card shadow-sm hover:shadow-md transition-[color,background-color,border-color,box-shadow] duration-300 relative overflow-hidden group">
                <div className="h-1 w-full bg-gradient-to-r from-primary via-accent to-primary opacity-80 group-hover:opacity-100 transition-opacity" />

                <CardHeader className="pb-4 pt-6">
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="outline" className="text-xs font-semibold bg-secondary/50 border-accent/20 text-foreground">
                      {item.highlightTag}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono font-medium">0{i + 1}</span>
                  </div>
                  <CardTitle className="text-xl font-semibold font-default text-foreground">
                    {item.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex-1 flex flex-col gap-5 justify-between">
                  <div className="rounded-lg p-4 bg-muted/50 border border-border/60">
                    <div className="flex items-start gap-2.5">
                      <div className="rounded-full bg-primary/10 p-1 text-primary shrink-0 mt-0.5">
                        <Check className="w-4 h-4 stroke-[2.5]" />
                      </div>
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-primary block mb-1">
                          Working With Me
                        </span>
                        <p className="text-sm text-foreground/90 leading-relaxed font-default">
                          {item.consultantBenefit}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg p-4 bg-background/50 border border-border/40">
                    <div className="flex items-start gap-2.5">
                      <div className="rounded-full bg-destructive/10 p-1 text-destructive shrink-0 mt-0.5">
                        <X className="w-4 h-4 stroke-[2.5]" />
                      </div>
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
                          Traditional Agency
                        </span>
                        <p className="text-sm text-muted-foreground leading-relaxed font-default">
                          {item.agencyDrawback}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-16 rounded-2xl bg-card border border-border/80 p-8 sm:p-10 lg:p-12 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-8 -mr-8 w-48 h-48 bg-secondary/80 rounded-full blur-2xl pointer-events-none" />
          
          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl space-y-4 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 text-accent font-semibold text-sm tracking-wide">
                <Award className="w-4 h-4" />
                <span>Personal Commitment To Your Search Growth</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-semibold font-default text-foreground">
                {"\"No middlemen, no delegates. You gain direct expertise on every call.\""}
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                I take personal accountability for every technical audit, strategy document, and link building tactic implemented on your site. When you have a urgent question, you ask me directly—not a support desk ticket queue.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 shrink-0 w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto font-medium px-6 py-6 shadow-md" asChild>
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="w-full sm:w-auto font-medium px-6 py-6 border-border" asChild>
                <Link to="/CaseStudiesPage">
                  <span>Explore Genuine Case Studies</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-6 sm:gap-10 text-xs sm:text-sm text-muted-foreground font-medium">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <span>Honest & Transparent Reporting</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-accent" />
            <span>Agile Technical Implementation</span>
          </div>
          <div className="flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-primary" />
            <span>Direct Access via Slack & Email</span>
          </div>
        </div>
      </div>
    </section>;
}